import { useToast } from '@monorepo/shared/contexts/ToastContext';
import { useModal } from '@monorepo/shared/hooks/useModalV2';
import React, { useCallback } from 'react';
import { useSetUserIsOrganizationAdmin } from './useSetUserIsOrganizationAdmin';

type UseConfirmSetUserIsOrganizationAdminParams = {
  organizationId: string;
  projectId: string;
  userId: string;
  userName: string;
  isSettingOrgAdmin: boolean;
};

export function useConfirmSetUserIsOrganizationAdmin({
  organizationId,
  projectId,
  userId,
  userName,
  isSettingOrgAdmin,
}: UseConfirmSetUserIsOrganizationAdminParams) {
  const { confirm } = useModal();
  const { showUserFriendlyErrorToast, success } = useToast();
  const [setUserIsOrganizationAdmin] = useSetUserIsOrganizationAdmin({
    projectId,
    config: { throwOnError: true },
  });

  const handleConfirm = useCallback(
    async (isConfirmed: boolean) => {
      if (isConfirmed) {
        await setUserIsOrganizationAdmin({
          organizationId,
          userId,
          isOrgAdmin: isSettingOrgAdmin,
        });
      }
    },
    [isSettingOrgAdmin, organizationId, setUserIsOrganizationAdmin, userId],
  );

  return useCallback(async () => {
    try {
      const isConfirmed = await confirm({
        title: 'Are you sure?',
        description: isSettingOrgAdmin ? (
          <>
            <b>{userName}</b> will be able to see every site in this
            organization, including sites they cannot access today. They will
            also have Site Admin access to every site in this organization.
          </>
        ) : (
          <>
            <b>{userName}</b> will lose access to every site in this
            organization except the ones they are assigned to directly, and
            their role on each site will go back to the one assigned to them
            previously.
          </>
        ),
        cancelButtonText: 'Cancel',
        confirmButtonText: isSettingOrgAdmin
          ? 'Make Organization Admin'
          : 'Revoke Organization Admin',
        afterConfirmButtonText: 'Updating...',
        danger: !isSettingOrgAdmin,
        onConfirmAsync: handleConfirm,
      });
      if (isConfirmed) {
        success(
          isSettingOrgAdmin
            ? `${userName} is now an organization admin.`
            : `${userName} is no longer an organization admin.`,
        );
      }
    } catch (err) {
      showUserFriendlyErrorToast(
        err,
        isSettingOrgAdmin
          ? `Unable to make ${userName} an organization admin.`
          : `Unable to revoke ${userName}'s organization admin access.`,
      );
    }
  }, [
    confirm,
    handleConfirm,
    isSettingOrgAdmin,
    showUserFriendlyErrorToast,
    success,
    userName,
  ]);
}
